import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { RegistryLookupService } from '../tokens';
import {
  OnrcLookupResult,
  OnrcData,
  BnrLookupResult,
  AsfLookupResult,
  UnbrLookupResult,
  BlacklistResult,
  CicResult,
  FinancialsResult,
  RiskScoreResult,
  Psd2ConnectResult,
  EntityData
} from '../models';
import { AnonymousAuthService } from './anonymous-auth.service';
import { API_PATHS } from '../config/api-paths';
import { cnpLast4FromRegistry } from '../util/onrc-representative';

/**
 * Shape of one entry inside `values.content[]` from the datacollector
 * company-info endpoint. Field names are as returned by the backend
 * (PascalCase, per Rule 01).
 */
interface CompanyInfoEntry {
  Id: string;
  ParentId: string;
  Indentificator: string;
  TipEntitate: string;
  Tip: string;
  Nume: string;
  FormaLegala: string;
  CAEN: string;
  CAENDescriere: string;
  CapitalSocial: string;
  RegistrationNumber: string;
  Status: string;
  DataInregistrare: string;
  Judet: string;
  Oras: string;
  Street: string;
  StreetNo: string;
  Bloc: string;
  Sc: string;
  Etaj: string;
  Ap: string;
  PostalCode: string;
  Calitate: string;
  LastName: string;
  FirstName: string;
  MiddleName: string;
  BirthDate: string;
  DataStart: string;
  DataStop: string;
  DataNumire: string;
  Procent: string;
  Financials: {
    FinancialDate: string;
    CifraAfaceri: string;
    Profit_Pierdere_Net: string;
    TotalCapitaluriProprii: string;
    NumarAngajati: string;
  };
}

/**
 * Top-level envelope returned by `GET /services/datacollector/api/company-info/{cui}`.
 *
 * `values.content` is the parsed company record. When datacollector cannot parse the
 * provider answer it emits `values.xmlData_raw` instead (the raw provider payload) and no
 * `content` — a data-processing failure on the collector side, NOT a non-existent company
 * (EM-1270). The two cases must be reported differently to the applicant.
 */
interface CompanyInfoEnvelope {
  created: string;
  id: string | null;
  identifier: string;
  message: string;
  status: 'COMPLETED' | 'RUNNING' | 'ERROR';
  type: string;
  values: {
    error: string;
    info: string;
    timespan: string;
    hasData: boolean;
    content?: CompanyInfoEntry[];
    xmlData_raw?: string;
  };
}

/** Build a human-readable address from the ONRC address fields. */
function buildAddress(entry: CompanyInfoEntry): string {
  const parts: string[] = [];
  if (entry.Street) {
    let street = `Str. ${entry.Street}`;
    if (entry.StreetNo) street += ` nr. ${entry.StreetNo}`;
    if (entry.Bloc) street += `, bl. ${entry.Bloc}`;
    if (entry.Sc) street += `, sc. ${entry.Sc}`;
    if (entry.Etaj) street += `, et. ${entry.Etaj}`;
    if (entry.Ap) street += `, ap. ${entry.Ap}`;
    parts.push(street);
  }
  if (entry.Oras) parts.push(entry.Oras);
  if (entry.Judet) parts.push(`Jud. ${entry.Judet}`);
  if (entry.PostalCode) parts.push(entry.PostalCode);
  return parts.join(', ');
}

/**
 * ONRC roles that make a person a legal representative (BRS §2.1.6 — "director/administrator
 * data"; `presedinte` is the chair of the board of administrators). Auditors (`auditor`) and
 * censors (`cenzor`) share the registry's `Administrator` row type but represent no one.
 */
const REPRESENTATIVE_ROLE = /administr|director|pre[sș]edinte/i;
const NON_REPRESENTATIVE_ROLE = /auditor|cenzor/i;

/**
 * A role the registry glued onto the end of a name ("LESE FLAVIU TEOFIL DIRECTOR GENERAL") — the
 * same person's shareholder row carries the clean name, so the suffix is registry noise.
 */
const TRAILING_ROLE = /\s+(director(\s+(general|executiv|economic|adjunct|financiar))?|administrator(\s+unic)?|pre[sș]edinte)\s*$/i;

/** Strips any role text glued onto the end of a registry name, keeping the name itself. */
function representativeName(raw: string): string {
  let name = (raw ?? '').trim();
  let stripped = name.replace(TRAILING_ROLE, '').trim();
  while (stripped && stripped !== name) {
    name = stripped;
    stripped = name.replace(TRAILING_ROLE, '').trim();
  }
  return name;
}

/**
 * EM-1868 — only a NATURAL person (`TipEntitate === 'PF'`) on an `Administrator` row in an
 * administrator / director role can be offered as the legal representative. Legal entities (an
 * audit firm is also an `Administrator` row) and auditors / censors are excluded.
 */
function isRepresentative(entry: CompanyInfoEntry): boolean {
  if (entry.Tip !== 'Administrator' || entry.TipEntitate?.toUpperCase() !== 'PF') return false;
  const role = entry.Calitate?.trim() ?? '';
  if (NON_REPRESENTATIVE_ROLE.test(role)) return false;
  return !role || REPRESENTATIVE_ROLE.test(role);
}

/**
 * Maps the Rule-01 datacollector envelope to `OnrcData`.
 *
 * - The row with `Tip === 'StartEntity'` is the company being queried.
 * - Legal-representative candidates are natural persons on `Administrator` rows in an
 *   administrator / director role (`isRepresentative`, EM-1868).
 * - The pre-login caller receives each person's identifier masked to its last four digits; only
 *   those four digits are kept (`cnpLast4`) — the portal never holds a full CNP from the registry.
 * - A person listed twice is shown once (keyed by cleaned name + CNP hint).
 */
function mapEnvelopeToOnrcData(envelope: CompanyInfoEnvelope): OnrcData | null {
  const content = envelope.values?.content ?? [];
  if (!content.length) return null;

  const company = content.find(e => e.Tip === 'StartEntity');
  if (!company) return null;

  const directorMap = new Map<string, { name: string; cnpLast4: string; position: string }>();
  for (const entry of content) {
    if (!isRepresentative(entry)) continue;
    const name = representativeName(entry.Nume);
    if (!name) continue;
    const cnpLast4 = cnpLast4FromRegistry(entry.Indentificator);
    const key = `${name.toUpperCase()}|${cnpLast4}`;
    if (!directorMap.has(key)) {
      directorMap.set(key, {
        name,
        cnpLast4,
        position: entry.Calitate?.trim() || 'administrator',
      });
    }
  }

  const caenCode = company.CAEN
    ? `${company.CAEN}${company.CAENDescriere ? ' - ' + company.CAENDescriere : ''}`
    : '';

  // Build a street string from the discrete address fields
  const streetParts: string[] = [];
  if (company.Street) streetParts.push(company.Street);
  if (company.StreetNo) streetParts.push(`nr. ${company.StreetNo}`);
  if (company.Bloc) streetParts.push(`bl. ${company.Bloc}`);
  if (company.Sc) streetParts.push(`sc. ${company.Sc}`);
  if (company.Etaj) streetParts.push(`et. ${company.Etaj}`);
  if (company.Ap) streetParts.push(`ap. ${company.Ap}`);

  const fin = company.Financials ?? null;
  const revenueLastFy = fin?.CifraAfaceri ? Number(fin.CifraAfaceri) : null;
  const numberOfEmployees = fin?.NumarAngajati ? Number(fin.NumarAngajati) : null;

  return {
    companyName:         company.Nume,
    registrationNumber:  company.RegistrationNumber,
    legalForm:           company.FormaLegala ?? '',
    caenCode,
    registeredAddress:   buildAddress(company),
    incorporationDate:   company.DataInregistrare ?? '',
    shareCapital:        Number(company.CapitalSocial) || 0,
    directors:           Array.from(directorMap.values()),
    // Discrete address fields for the registration DTO
    addressStreet:       streetParts.join(', '),
    addressStreetExtra:  '',
    addressCity:         company.Oras ?? '',
    addressCounty:       company.Judet ?? '',
    addressPostalCode:   company.PostalCode ?? '',
    // Financials snapshot (Rule 01 — datacollector returns the latest available year only)
    revenueLastFy:       Number.isFinite(revenueLastFy) ? revenueLastFy : null,
    revenueLastFyDate:   fin?.FinancialDate || null,
    numberOfEmployees:   Number.isFinite(numberOfEmployees) ? numberOfEmployees : null,
  };
}

/**
 * REST-backed implementation of `RegistryLookupService`.
 *
 * `lookupCui` hits the real datacollector endpoint with an anonymous token
 * (Rule 01 + Rule 03).
 */
@Injectable({ providedIn: 'root' })
export class RegistryLookupRestService implements RegistryLookupService {
  private readonly http          = inject(HttpClient);
  private readonly anonymousAuth = inject(AnonymousAuthService);

  lookupCui(cui: string): Observable<OnrcLookupResult> {
    // Strip any RO prefix — datacollector expects a numeric-only CUI
    const numericCui = cui.toUpperCase().replace(/^RO\s*/i, '').trim();

    return this.anonymousAuth.getToken().pipe(
      switchMap(token =>
        this.http.get<CompanyInfoEnvelope>(
          `${API_PATHS.COMPANY_INFO_PATH}/${numericCui}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
      ),
      map((envelope): OnrcLookupResult => {
        // The registry genuinely has no record for this CUI.
        if (envelope.status !== 'COMPLETED' || !envelope.values?.hasData) {
          return { success: false, error: 'errors.registry.notFound' };
        }
        const data = mapEnvelopeToOnrcData(envelope);
        if (!data) {
          // Data WAS returned for this CUI but could not be read (e.g. datacollector fell
          // back to `values.xmlData_raw`). Telling the applicant the company does not exist
          // would be wrong — it does, and there is nothing they can do about it (EM-1270).
          return { success: false, error: 'errors.registry.dataUnreadable' };
        }
        return { success: true, data };
      }),
      catchError(() => of<OnrcLookupResult>({ success: false, error: 'errors.registry.lookupFailed' })),
    );
  }
}
